import { createAction, handleActions } from "redux-actions";
import {
    getEnginesAPI,
    getAgentsAPI,
    getJobsAPI,
    getJobDetailAPI,
    updateSourcesAPI,
    getJobStatusAPI,
    getSourceDetailAPI,
    createJobsAPI,
    getSessionsAPI,
    createSessionAPI,
    createSessionChatAPI,
    getSessionChatsAPI,
    retrySessionChatAPI,
} from "../modules/apis/ai-workspace";
import Polling from "@cores/polling";
import { nanoid } from "nanoid";
import * as yup from "yup";

/**
 * JSON Schema를 Yup 스키마로 변환
 */
const jsonSchemaToYup = (schema) => {
    if (!schema) return yup.mixed();

    // required 배열 추출 (1depth에 있는 경우도 있지만, properties에 다른 프로퍼티와 함께 있는 경우도 있어서 양쪽 다 체크)
    const requiredFields =
        schema.required || (Array.isArray(schema.properties?.required) ? schema.properties.required : []);

    switch (schema.type) {
        case "string":
            return yup.string();
        case "number":
        case "integer": {
            return yup.number();
        }
        case "boolean":
            return yup.boolean();
        case "object": {
            const shape = {};
            if (schema.properties) {
                for (const [key, propSchema] of Object.entries(schema.properties)) {
                    // 'required'가 배열인 경우 스킵 (비표준 위치의 필수 필드 배열)
                    if (key === "required" && Array.isArray(propSchema)) continue;

                    let fieldSchema = jsonSchemaToYup(propSchema);
                    if (requiredFields.includes(key)) {
                        fieldSchema = fieldSchema.required();
                    }
                    shape[key] = fieldSchema;
                }
            }
            return yup.object().shape(shape);
        }
        case "array":
            return yup.array().of(jsonSchemaToYup(schema.items));
        default:
            return yup.mixed();
    }
};

/**
 * Job 결과가 responseFormat에 정의된 스키마와 일치하는지 검증
 *
 * 잡이 COMPLETED로 끝나도 AI 엔진이 값을 채우지 못한 채 요청 스키마를 그대로 돌려주는
 * 경우가 있다. 이때 화면은 결과를 해석할 수 없으므로 ERROR로 내려가는데, 재시도해도 같은
 * 결과가 나오므로 실제 요청 실패와 구분해 안내해야 한다 (isInvalidResultFormat 참고).
 */
const validateJobResultFormat = (job) => {
    const schema = job?.request?.responseFormat?.jsonSchema?.schema;

    // responseFormat이 없으면 무조건 통과
    if (!schema) {
        return true;
    }

    // result가 null이거나 undefined인 경우 실패
    if (job?.result == null) {
        return false;
    }

    // result.message가 없거나 객체가 아닌 경우 실패
    const message = job.result.message;
    if (message == null || typeof message !== "object" || Array.isArray(message)) {
        return false;
    }

    try {
        const yupSchema = jsonSchemaToYup(schema);
        yupSchema.validateSync(message);
        return true;
    } catch (error) {
        console.error("Validation error:", error.message, error.path);
        return false;
    }
};

// Action Types
const GET_AI_WORKSPACE_ENGINES_PENDING = "GET_AI_WORKSPACE_ENGINES_PENDING";
const GET_AI_WORKSPACE_ENGINES_SUCCESS = "GET_AI_WORKSPACE_ENGINES_SUCCESS";
const GET_AI_WORKSPACE_ENGINES_FAILURE = "GET_AI_WORKSPACE_ENGINES_FAILURE";

const GET_AI_WORKSPACE_AGENTS_PENDING = "GET_AI_WORKSPACE_AGENTS_PENDING";
const GET_AI_WORKSPACE_AGENTS_SUCCESS = "GET_AI_WORKSPACE_AGENTS_SUCCESS";
const GET_AI_WORKSPACE_AGENTS_FAILURE = "GET_AI_WORKSPACE_AGENTS_FAILURE";

const GET_AI_WORKSPACE_JOBS_PENDING = "GET_AI_WORKSPACE_JOBS_PENDING";
const GET_AI_WORKSPACE_JOBS_SUCCESS = "GET_AI_WORKSPACE_JOBS_SUCCESS";
const GET_AI_WORKSPACE_JOBS_FAILURE = "GET_AI_WORKSPACE_JOBS_FAILURE";

const GET_AI_WORKSPACE_JOB_DETAIL_PENDING = "GET_AI_WORKSPACE_JOB_DETAIL_PENDING";
const GET_AI_WORKSPACE_JOB_DETAIL_SUCCESS = "GET_AI_WORKSPACE_JOB_DETAIL_SUCCESS";
const GET_AI_WORKSPACE_JOB_DETAIL_FAILURE = "GET_AI_WORKSPACE_JOB_DETAIL_FAILURE";
const GET_AI_WORKSPACE_SESSION_DETAIL_SUCCESS = "GET_AI_WORKSPACE_SESSION_DETAIL_SUCCESS";

const CREATE_AI_WORKSPACE_JOB_PENDING = "CREATE_AI_WORKSPACE_JOB_PENDING";
const CREATE_AI_WORKSPACE_JOB_SUCCESS = "CREATE_AI_WORKSPACE_JOB_SUCCESS";
const CREATE_AI_WORKSPACE_JOB_FAILURE = "CREATE_AI_WORKSPACE_JOB_FAILURE";
const CREATE_AI_WORKSPACE_SESSION_PENDING = "CREATE_AI_WORKSPACE_SESSION_PENDING";
const CREATE_AI_WORKSPACE_SESSION_SUCCESS = "CREATE_AI_WORKSPACE_SESSION_SUCCESS";

const RESET_AI_WORKSPACE_JOB_DETAIL = "RESET_AI_WORKSPACE_JOB_DETAIL";
const RESET_AI_WORKSPACE_JOBS = "RESET_AI_WORKSPACE_JOBS";
const RESET_AI_WORKSPACE = "RESET_AI_WORKSPACE";

const UPDATE_AI_WORKSPACE_CREATE_JOBS = "UPDATE_AI_WORKSPACE_CREATE_JOBS";
const UPDATE_AI_WORKSPACE_SELECTED_JOB = "UPDATE_AI_WORKSPACE_SELECTED_JOB";
const UPDATE_AI_WORKSPACE_CHAT_RESPONSE = "UPDATE_AI_WORKSPACE_CHAT_RESPONSE";

const CREATE_AI_WORKSPACE_CHAT_PENDING = "CREATE_AI_WORKSPACE_CHAT_PENDING";
const RETRY_AI_WORKSPACE_CHAT_PENDING = "RETRY_AI_WORKSPACE_CHAT_PENDING";
const CREATE_AI_WORKSPACE_CHAT_SUCCESS = "CREATE_AI_WORKSPACE_CHAT_SUCCESS";
const CREATE_AI_WORKSPACE_CHAT_FAILURE = "CREATE_AI_WORKSPACE_CHAT_FAILURE";

// Initial State
const initialState = {
    pending: false,
    error: false,
    engines: { pending: false, data: null, error: false },
    agents: { pending: false, data: null, error: false },
    jobs: { pending: false, data: [], error: false },
    jobDetail: { pending: false, data: null, error: false, createChat: { pending: false, data: null, error: false } },
    createJobs: [],
    selectedJob: null,
};

// Reducer
export default handleActions(
    {
        [GET_AI_WORKSPACE_ENGINES_PENDING]: (state) => {
            return {
                ...state,
                engines: {
                    ...state.engines,
                    pending: true,
                    error: false,
                },
            };
        },
        [GET_AI_WORKSPACE_ENGINES_SUCCESS]: (state, action) => {
            const { results } = action.payload || {};
            const newEngines = (state.engines.data || []).concat(results || []);
            return {
                ...state,
                engines: {
                    ...state.engines,
                    pending: false,
                    error: false,
                    data: newEngines,
                },
            };
        },
        [GET_AI_WORKSPACE_ENGINES_FAILURE]: (state) => {
            return {
                ...state,
                engines: {
                    ...state.engines,
                    pending: false,
                    error: true,
                },
            };
        },
        [GET_AI_WORKSPACE_AGENTS_PENDING]: (state) => {
            return {
                ...state,
                agents: {
                    ...state.agents,
                    pending: true,
                    error: false,
                },
            };
        },
        [GET_AI_WORKSPACE_AGENTS_SUCCESS]: (state, action) => {
            const { results } = action.payload || {};
            const newAgents = (state.agents.data || []).concat(results || []);
            return {
                ...state,
                agents: {
                    ...state.agents,
                    pending: false,
                    error: false,
                    data: newAgents,
                },
            };
        },
        [GET_AI_WORKSPACE_AGENTS_FAILURE]: (state) => {
            return {
                ...state,
                agents: {
                    ...state.agents,
                    pending: false,
                    error: true,
                },
            };
        },
        [GET_AI_WORKSPACE_JOBS_PENDING]: (state) => {
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    pending: true,
                    error: false,
                },
            };
        },
        [GET_AI_WORKSPACE_JOBS_SUCCESS]: (state, action) => {
            const { results, nextToken } = action.payload || {};
            const newJobs = state.jobs.data?.concat(results || []);
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    pending: false,
                    error: false,
                    data: newJobs,
                    nextToken,
                },
            };
        },
        [GET_AI_WORKSPACE_JOBS_FAILURE]: (state) => {
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    pending: false,
                    error: true,
                },
            };
        },
        [GET_AI_WORKSPACE_JOB_DETAIL_PENDING]: (state, action) => {
            const { isFetchMore } = action.payload || {};
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data: isFetchMore ? state.jobDetail.data : null,
                    pending: true,
                    error: false,
                },
            };
        },
        [GET_AI_WORKSPACE_JOB_DETAIL_SUCCESS]: (state, action) => {
            const isValidMessage = validateJobResultFormat(action.payload);
            if (!isValidMessage) {
                action.payload.status = "ERROR";
                action.payload.isInvalidResultFormat = true;
            }

            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    pending: false,
                    error: false,
                    data: action.payload,
                    createChat: { pending: false, data: null, error: false },
                },
            };
        },
        [GET_AI_WORKSPACE_SESSION_DETAIL_SUCCESS]: (state, action) => {
            try {
                const { results, nextToken } = action.payload;
                return {
                    ...state,
                    jobDetail: {
                        ...state.jobDetail,
                        pending: false,
                        error: false,
                        data: state.jobDetail.data ? [...state.jobDetail.data, ...results] : results,
                        nextToken: nextToken ?? null,
                        createChat: { pending: false, data: null, error: false },
                    },
                };
            } catch (e) {
                console.log(e);
            }
        },
        [GET_AI_WORKSPACE_JOB_DETAIL_FAILURE]: (state) => {
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    pending: false,
                    error: true,
                },
            };
        },
        [CREATE_AI_WORKSPACE_JOB_PENDING]: (state, action) => {
            const { request, createJobKey } = action.payload;
            const createJobs = state.createJobs.map((job) => ({ ...job, isLatest: false }));
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data: { request },
                },
                createJobs: [
                    {
                        key: createJobKey,
                        isLatest: true,
                        pending: true,
                        error: false,
                    },
                    ...createJobs,
                ],
            };
        },
        [CREATE_AI_WORKSPACE_SESSION_PENDING]: (state, action) => {
            const { request, createJobKey } = action.payload;
            const createJobs = state.createJobs.map((job) => ({ ...job, isLatest: false }));
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data: [request],
                },
                createJobs: [
                    {
                        key: createJobKey,
                        isLatest: true,
                        pending: true,
                        error: false,
                    },
                    ...createJobs,
                ],
            };
        },
        [CREATE_AI_WORKSPACE_JOB_SUCCESS]: (state, action) => {
            const { data, createJobKey, requestPayload } = action.payload;

            const isValidMessage = validateJobResultFormat(data);
            if (!isValidMessage) {
                data.status = "ERROR";
                data.isInvalidResultFormat = true;
            }

            const createJobs = state.createJobs.map((job) => {
                if (job.key === createJobKey)
                    return {
                        ...job,
                        pending: false,
                        requestPayload,
                        jobStatus: isValidMessage ? job.jobStatus : "ERROR",
                    };
                else return job;
            });

            const jobsData = state.jobs.data?.find((job) => job.id === data.id)
                ? state.jobs.data?.map((job) => (job.id === data.id ? data : job))
                : [data, ...state.jobs.data];
            const isSelectedJob =
                !state.selectedJob && state.createJobs.find((job) => job.key === createJobKey && job.isLatest);
            const jobData = isSelectedJob ? data : state.jobDetail.data;
            return {
                ...state,
                createJobs,
                jobs: {
                    ...state.jobs,
                    data: jobsData,
                },
                jobDetail: {
                    ...state.jobDetail,
                    data: jobData,
                },
                selectedJob: isSelectedJob ? data : state.selectedJob,
            };
        },
        [CREATE_AI_WORKSPACE_SESSION_SUCCESS]: (state, action) => {
            const { data, createJobKey, requestPayload, isSelectedJob } = action.payload;
            const createJobs = state.createJobs.map((job) => {
                if (job.key === createJobKey) return { ...job, id: data.session.id, requestPayload };
                else return job;
            });
            return {
                ...state,
                createJobs,
                jobDetail: !state.selectedJob
                    ? {
                          ...state.jobDetail,
                          data: isSelectedJob ? [data] : state.jobDetail.data,
                      }
                    : { ...state.jobDetail },
            };
        },
        [CREATE_AI_WORKSPACE_JOB_FAILURE]: (state, action) => {
            const { error, createJobKey, requestPayload } = action.payload;
            const createJobs = state.createJobs.map((job) => {
                if (job.key === createJobKey) return { ...job, pending: false, error, requestPayload };
                else return job;
            });
            return {
                ...state,
                createJobs,
            };
        },
        [UPDATE_AI_WORKSPACE_CREATE_JOBS]: (state, action) => {
            const { createJobKey, ...restPayload } = action.payload;
            const createJobs = state.createJobs.map((job) => {
                if (job.key === createJobKey) return { ...job, ...restPayload };
                else return job;
            });
            return {
                ...state,
                createJobs,
            };
        },
        [RESET_AI_WORKSPACE_JOB_DETAIL]: (state) => {
            return {
                ...state,
                jobDetail: {
                    ...initialState.jobDetail,
                },
            };
        },
        [RESET_AI_WORKSPACE_JOBS]: (state) => {
            return {
                ...state,
                jobs: {
                    ...initialState.jobs,
                },
                jobDetail: {
                    ...initialState.jobDetail,
                },
            };
        },
        [RESET_AI_WORKSPACE]: (state) => {
            const { ...restStates } = initialState;
            return {
                ...state,
                ...restStates,
            };
        },
        [UPDATE_AI_WORKSPACE_SELECTED_JOB]: (state, action) => {
            return {
                ...state,
                selectedJob: action.payload,
            };
        },
        [CREATE_AI_WORKSPACE_CHAT_PENDING]: (state, action) => {
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data: [{ ...action.payload, actor: "USER" }, ...(state.jobDetail.data || [])],
                    createChat: {
                        ...state.jobDetail.createChat,
                        pending: true,
                    },
                },
            };
        },
        [RETRY_AI_WORKSPACE_CHAT_PENDING]: (state, action) => {
            const { failedChat } = action.payload;
            const data = state.jobDetail.data.filter((d) => d.id !== failedChat.id);
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data,
                    createChat: {
                        ...state.jobDetail.createChat,
                        pending: true,
                    },
                },
            };
        },
        [CREATE_AI_WORKSPACE_CHAT_SUCCESS]: (state, action) => {
            const jobDetailData = state.jobDetail.data || [];
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data: [{ ...jobDetailData[0], id: action.payload.id }, ...jobDetailData.slice(1)],
                    createChat: {
                        ...state.jobDetail.createChat,
                        data: action.payload,
                    },
                },
            };
        },
        [CREATE_AI_WORKSPACE_CHAT_FAILURE]: (state, action) => {
            const { failedChat } = action.payload || [];
            return {
                ...state,
                jobDetail: {
                    ...state.jobDetail,
                    data: [failedChat, ...state.jobDetail.data],
                    createChat: {
                        ...state.jobDetail.createChat,
                        error: true,
                        pending: false,
                    },
                },
            };
        },
        [UPDATE_AI_WORKSPACE_CHAT_RESPONSE]: (state, action) => {
            //NOTE: 웹소켓 대화 응답 업데이트
            const { data } = action.payload;
            const { chatId, sessionId, pairId, prompt, status } = data;

            // 새로운 채팅 응답 데이터 생성
            const newChatResponse = {
                id: chatId,
                session: { id: sessionId },
                actor: "AGENT",
                prompt,
                status,
            };

            // 기존 채팅 목록에 새 응답 삽입 (pairId와 매칭되는 위치 앞에)
            const updatedChatList = (state.jobDetail.data ?? []).reduce((acc, cur) => {
                const isExist = state.jobDetail.data?.some((chat) => chat.id === newChatResponse.id);
                if (!isExist && cur.session.id === sessionId && cur.id === pairId) {
                    return [...acc, newChatResponse, cur];
                }
                return [...acc, cur];
            }, []);

            // createJob 찾기 및 pending 상태 업데이트
            const targetCreateJob = state.createJobs.find((job) => job.id === sessionId);
            const updatedCreateJobs = state.createJobs.map((job) =>
                job.id === sessionId ? { ...job, pending: false } : job,
            );

            // 세션 데이터 생성
            const sessionJobData = {
                id: targetCreateJob?.id,
                name: targetCreateJob?.requestPayload?.sessionData?.name,
            };

            // jobs 목록에 세션이 없으면 추가
            const isSessionJobExists = state.jobs.data.some((job) => job.id === sessionId);
            const updatedSessionJobsData = isSessionJobExists ? state.jobs.data : [sessionJobData, ...state.jobs.data];

            // 선택된 job이 현재 세션인 경우에만 jobDetail 업데이트
            const isSelectedSessionJob =
                (!state.selectedJob && state.createJobs.some((job) => job.isLatest && job.id === sessionId)) ||
                state.selectedJob?.id === sessionId;
            const updatedSessionJobDetail = isSelectedSessionJob
                ? {
                      ...state.jobDetail,
                      data: updatedChatList,
                      createChat: {
                          ...state.jobDetail.createChat,
                          data,
                          pending: false,
                      },
                  }
                : state.jobDetail;

            const selectedJob =
                !state.selectedJob && state.createJobs.some((job) => job.isLatest && job.id === sessionId)
                    ? sessionJobData
                    : state.selectedJob;

            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    data: updatedSessionJobsData,
                },
                jobDetail: updatedSessionJobDetail,
                createJobs: updatedCreateJobs,
                selectedJob,
            };
        },
    },
    initialState,
);

// Actions
export const resetState = createAction(RESET_AI_WORKSPACE);
export const resetJobs = createAction(RESET_AI_WORKSPACE_JOBS);
export const resetJobDetail = createAction(RESET_AI_WORKSPACE_JOB_DETAIL);
export const updateSelectedJob = createAction(UPDATE_AI_WORKSPACE_SELECTED_JOB);
export const updateAiWorkspaceChatResponse = createAction(UPDATE_AI_WORKSPACE_CHAT_RESPONSE);

// Dispatch Functions
export const getEngines =
    (params = {}) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();

        dispatch({ type: GET_AI_WORKSPACE_ENGINES_PENDING });

        try {
            let nextToken = null;

            do {
                const response = await getEnginesAPI(stage.id, stage.endpoint, project.id, {
                    ...params,
                    nextToken,
                });

                nextToken = response.data.nextToken;
                // 첫 번째 호출 시 초기 데이터 설정, 이후 호출 시 append
                dispatch({
                    type: GET_AI_WORKSPACE_ENGINES_SUCCESS,
                    payload: response.data,
                });
            } while (nextToken);
        } catch (error) {
            dispatch({
                type: GET_AI_WORKSPACE_ENGINES_FAILURE,
                payload: error,
            });
            throw error;
        }
    };

export const getAgents =
    (params = {}) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();

        dispatch({ type: GET_AI_WORKSPACE_AGENTS_PENDING });

        try {
            let nextToken = null;

            do {
                const response = await getAgentsAPI(stage.id, stage.endpoint, project.id, {
                    limit: 20,
                    status: "ACTIVE",
                    ...params,
                    nextToken,
                });

                nextToken = response.data.nextToken;
                dispatch({
                    type: GET_AI_WORKSPACE_AGENTS_SUCCESS,
                    payload: response.data,
                });
            } while (nextToken);
        } catch (error) {
            dispatch({
                type: GET_AI_WORKSPACE_AGENTS_FAILURE,
                payload: error,
            });
            throw error;
        }
    };

export const getJobs =
    (params = {}, signal) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();
        dispatch({ type: GET_AI_WORKSPACE_JOBS_PENDING });
        try {
            const response = await getJobsAPI(stage.id, stage.endpoint, project.id, params, signal);
            dispatch({
                type: GET_AI_WORKSPACE_JOBS_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_AI_WORKSPACE_JOBS_FAILURE,
                payload: error,
            });
        }
    };

export const getJobDetail =
    (jobId, params = {}, signal) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();

        dispatch({ type: GET_AI_WORKSPACE_JOB_DETAIL_PENDING });

        try {
            const response = await getJobDetailAPI(stage.id, stage.endpoint, project.id, jobId, params, signal);

            dispatch({
                type: GET_AI_WORKSPACE_JOB_DETAIL_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_AI_WORKSPACE_JOB_DETAIL_FAILURE,
                payload: error,
            });
            throw error;
        }
    };

export const checkSourcesPolling =
    ({ domain, resourceId, sourceId, createJobKey }) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();

        return new Promise((resolve, reject) => {
            let resolved = false;

            try {
                Polling(
                    async () => {
                        const currentResourceId =
                            domain === "VIDEO" ? getState().video.data?.id : getState().asset.data?.id;

                        if (resourceId !== currentResourceId) {
                            return null;
                        }
                        return await getSourceDetailAPI(stage.id, stage.endpoint, project.id, sourceId);
                    },
                    (response) => {
                        if (!response) {
                            return true;
                        }

                        const currentResourceId =
                            domain === "VIDEO" ? getState().video.data?.id : getState().asset.data?.id;

                        if (resourceId !== currentResourceId) {
                            return true;
                        }

                        const isInProgress = !["READY", "CANCELED"].includes(response.data.status);
                        const shouldStop = !isInProgress || !resourceId;

                        dispatch({
                            type: UPDATE_AI_WORKSPACE_CREATE_JOBS,
                            payload: { sourceDetail: response.data, createJobKey },
                        });

                        // Polling이 완료되면 resolve
                        if (shouldStop && !resolved) {
                            resolved = true;
                            resolve(response.data);
                        }

                        return shouldStop;
                    },
                    (response) => {
                        const currentResourceId =
                            domain === "VIDEO" ? getState().video.data?.id : getState().asset.data?.id;

                        if (resourceId !== currentResourceId) {
                            return;
                        }

                        // response.data 검증
                        if (!response || !response.data) {
                            console.error("checkSourcesPolling successCallback: response.data is missing", {
                                response,
                                resourceId,
                            });
                            return;
                        }

                        // console.log("checkSourcesPolling successCallback: sourceDetail received", {
                        //     sourceDetail: response.data,
                        //     hasStatus: !!response.data.status,
                        //     statusType: Array.isArray(response.data.status) ? "array" : typeof response.data.status,
                        //     statusLength: Array.isArray(response.data.status) ? response.data.status.length : undefined,
                        // });

                        dispatch({
                            type: UPDATE_AI_WORKSPACE_CREATE_JOBS,
                            payload: { sourceDetail: response.data, createJobKey },
                        });
                    },
                    (error) => {
                        if (!resolved) {
                            resolved = true;
                            reject(error);
                        }
                    },
                    4000,
                ).run();
            } catch (e) {
                if (!resolved) {
                    reject(e);
                }
            }
        });
    };

export const checkJobStatusPolling =
    ({ domain, resourceId, jobId, createJobKey }) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();

        return new Promise((resolve, reject) => {
            let resolved = false;

            try {
                Polling(
                    async () => {
                        const currentResourceId =
                            domain === "VIDEO" ? getState().video.data?.id : getState().asset.data?.id;

                        if (resourceId !== currentResourceId) {
                            return null;
                        }

                        return await getJobStatusAPI(stage.id, stage.endpoint, project.id, jobId);
                    },
                    (response) => {
                        if (!response) {
                            return true;
                        }

                        const currentResourceId =
                            domain === "VIDEO" ? getState().video.data?.id : getState().asset.data?.id;

                        if (resourceId !== currentResourceId) {
                            return null;
                        }

                        const isInProgress = !["CANCELED", "COMPLETED", "ERROR"].includes(response.data.status);
                        const shouldStop = !isInProgress || !jobId;

                        dispatch({
                            type: UPDATE_AI_WORKSPACE_CREATE_JOBS,
                            payload: { jobStatus: response.data, createJobKey },
                        });

                        // Polling이 완료되면 resolve
                        if (shouldStop && !resolved) {
                            resolved = true;
                            resolve(response.data);
                        }

                        return shouldStop;
                    },
                    (response) => {
                        const currentResourceId =
                            domain === "VIDEO" ? getState().video.data?.id : getState().asset.data?.id;

                        if (resourceId !== currentResourceId) {
                            return null;
                        }

                        dispatch({
                            type: UPDATE_AI_WORKSPACE_CREATE_JOBS,
                            payload: { jobStatus: response.data, createJobKey },
                        });
                        return response.data;
                    },
                    (error) => {
                        if (!resolved) {
                            resolved = true;
                            reject(error);
                        }
                    },
                    4000,
                ).run();
            } catch (e) {
                if (!resolved) {
                    reject(e);
                }
            }
        });
    };

export const createJob = (requestPayload) => async (dispatch, getState) => {
    const { stage, project } = getState();
    const { asset, engine, request, domain, resourceId } = requestPayload;

    const createJobKey = nanoid();
    const { responseFormat, prompt, name } = request;
    dispatch({
        type: CREATE_AI_WORKSPACE_JOB_PENDING,
        payload: { request, createJobKey },
    });
    try {
        // Update sources before creating a job
        const data = {
            action: "SYNC",
            data: {
                assetId: asset.id,
                elementId: asset.elements?.[0]?.id,
            },
        };
        const response = await updateSourcesAPI(stage.id, stage.endpoint, project.id, data);

        const sourceResult = response.data;

        const sourceDetail = await dispatch(
            checkSourcesPolling({ domain, resourceId, sourceId: sourceResult.id, createJobKey }),
        );

        if (sourceDetail.status === "READY") {
            let request = { prompt };
            if (responseFormat) request.responseFormat = responseFormat;
            const jobData = {
                name: name ?? prompt.substring(0, 100), // 100자 제한
                assetId: asset.id,
                sourceId: sourceResult.id,
                engineId: engine.id,
                request,
            };
            const jobResponse = await createJobsAPI(stage.id, stage.endpoint, project.id, jobData);
            const jobStatusResponse = await dispatch(
                checkJobStatusPolling({ domain, resourceId, jobId: jobResponse.data.id, createJobKey }),
            );

            let jobDetailResponse = null;
            if (["CANCELED", "COMPLETED", "ERROR"].includes(jobStatusResponse.status)) {
                jobDetailResponse = await getJobDetailAPI(stage.id, stage.endpoint, project.id, jobResponse.data.id);
            }

            if (jobDetailResponse) {
                dispatch({
                    type: CREATE_AI_WORKSPACE_JOB_SUCCESS,
                    payload: {
                        data: jobDetailResponse.data,
                        createJobKey,
                        requestPayload: { ...requestPayload, jobData },
                    },
                });
            }
        }
    } catch (error) {
        console.log(error);
        dispatch({
            type: CREATE_AI_WORKSPACE_JOB_FAILURE,
            payload: { error, createJobKey, requestPayload },
        });
    }
};

export const retryJobCreation = (requestPayload) => async (dispatch, getState) => {
    const { stage, project } = getState();
    const { request, domain, resourceId, jobData } = requestPayload;

    const createJobKey = nanoid();
    dispatch({ type: CREATE_AI_WORKSPACE_JOB_PENDING, payload: { request, createJobKey } });

    try {
        const jobResponse = await createJobsAPI(stage.id, stage.endpoint, project.id, jobData);
        const jobStatusResponse = await dispatch(
            checkJobStatusPolling({ domain, resourceId, jobId: jobResponse.data.id, createJobKey }),
        );

        let jobDetailResponse = null;
        if (["CANCELED", "COMPLETED", "ERROR"].includes(jobStatusResponse.status)) {
            jobDetailResponse = await getJobDetailAPI(stage.id, stage.endpoint, project.id, jobResponse.data.id);
        }

        if (jobDetailResponse) {
            dispatch({
                type: CREATE_AI_WORKSPACE_JOB_SUCCESS,
                payload: { data: jobDetailResponse.data, createJobKey, jobData },
            });
        }
    } catch (error) {
        dispatch({
            type: CREATE_AI_WORKSPACE_JOB_FAILURE,
            payload: { error, createJobKey, requestPayload },
        });
    }
};

export const getSessions =
    (params = {}, signal) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();
        dispatch({ type: GET_AI_WORKSPACE_JOBS_PENDING });

        try {
            const response = await getSessionsAPI(stage.id, stage.endpoint, project.id, params, signal);
            dispatch({
                type: GET_AI_WORKSPACE_JOBS_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_AI_WORKSPACE_JOBS_FAILURE,
                payload: error,
            });
        }
    };

export const createSessionJob = (requestPayload) => async (dispatch, getState) => {
    const { stage, project } = getState();
    const { asset, agentId, request, domain, resourceId } = requestPayload;

    const createJobKey = nanoid();
    const { responseFormat, prompt } = request;

    dispatch({
        type: CREATE_AI_WORKSPACE_SESSION_PENDING,
        payload: { request: { ...request, actor: "USER" }, createJobKey },
    });
    try {
        // Update sources before creating a job
        const data = {
            action: "SYNC",
            data: {
                assetId: asset.id,
                elementId: asset.elements?.[0]?.id,
            },
        };
        const response = await updateSourcesAPI(stage.id, stage.endpoint, project.id, data);

        const sourceResult = response.data;

        const sourceDetail = await dispatch(
            checkSourcesPolling({ domain, resourceId, sourceId: sourceResult.id, createJobKey }),
        );

        if (sourceDetail.status === "READY") {
            let request = { prompt };
            if (responseFormat) request.responseFormat = responseFormat;
            const sessionData = {
                name: prompt.substring(0, 100), // 100자 제한
                assetId: asset.id,
                sourceId: sourceResult.id,
                agentId,
            };
            const sessionResponse = await createSessionAPI(stage.id, stage.endpoint, project.id, sessionData);

            const chatResponse = await createSessionChatAPI(
                stage.id,
                stage.endpoint,
                project.id,
                sessionResponse.data.id,
                { prompt },
            );

            if (chatResponse) {
                const isSelectedJob =
                    !getState().aiWorkspace.jobs.data?.some((job) => job.id === sessionResponse.data.id) &&
                    getState().aiWorkspace.createJobs.some((job) => job.key === createJobKey && job.isLatest);

                dispatch({
                    type: CREATE_AI_WORKSPACE_SESSION_SUCCESS,
                    payload: {
                        data: {
                            id: chatResponse.data?.id,
                            session: { id: sessionResponse.data.id },
                            prompt,
                            name: prompt,
                            actor: "USER",
                        },
                        createJobKey,
                        isSelectedJob,
                        requestPayload: { ...requestPayload, sessionData },
                    },
                });
            }
        }
    } catch (error) {
        console.error(error);
        dispatch({
            type: CREATE_AI_WORKSPACE_JOB_FAILURE,
            payload: { error, createJobKey, requestPayload },
        });
    }
};

export const getSessionChats =
    (sessionId, params = {}, signal) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();

        dispatch({ type: GET_AI_WORKSPACE_JOB_DETAIL_PENDING, payload: { isFetchMore: !!params.nextToken } });

        try {
            const response = await getSessionChatsAPI(stage.id, stage.endpoint, project.id, sessionId, params, signal);

            dispatch({
                type: GET_AI_WORKSPACE_SESSION_DETAIL_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_AI_WORKSPACE_JOB_DETAIL_FAILURE,
                payload: error,
            });
        }
    };

export const createSessionChat =
    (sessionId, params = {}) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();
        const { prompt } = params;
        const chatData = {
            session: { id: sessionId },
            actor: "USER",
            prompt,
        };
        dispatch({ type: CREATE_AI_WORKSPACE_CHAT_PENDING, payload: chatData });

        try {
            const chatResponse = await createSessionChatAPI(stage.id, stage.endpoint, project.id, sessionId, params);
            dispatch({
                type: CREATE_AI_WORKSPACE_CHAT_SUCCESS,
                payload: chatResponse.data || [],
            });
        } catch (error) {
            dispatch({
                type: CREATE_AI_WORKSPACE_CHAT_FAILURE,
            });
        }
    };

export const retrySessionChat =
    (sessionId, params = {}) =>
    async (dispatch, getState) => {
        const { stage, project, aiWorkspace } = getState();

        const failedChat = aiWorkspace.jobDetail.data?.find((d, index) => d.status === "FAILED" && index === 0);
        dispatch({ type: RETRY_AI_WORKSPACE_CHAT_PENDING, payload: { failedChat } });

        try {
            await retrySessionChatAPI(stage.id, stage.endpoint, project.id, sessionId, params);
        } catch (error) {
            dispatch({
                type: CREATE_AI_WORKSPACE_CHAT_FAILURE,
                payload: { failedChat },
            });
        }
    };
